// Background Eraser tool — erases to transparency based on sampled color tolerance.
// Similar to Photoshop's Background Eraser: samples the color under the crosshair
// center and erases pixels within the tolerance range.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { stampLine, mergeBBox } from "../pixel/brush";
import type { BackgroundLayer, Layer, RasterLayer } from "../document/Layer";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface BackgroundEraserSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  tolerance: number;
  sampling: "continuous" | "once" | "background-swatch";
  limits: "discontiguous" | "contiguous" | "find-edges";
  /**
   * Photoshop's "Protect Foreground Color" option (audit i83): when enabled,
   * pixels whose color falls within `tolerance` of the current foreground
   * color are never erased, even if they otherwise match the sampled color.
   * Defaults to false to match Photoshop's default and preserve existing
   * behavior for callers that don't set it.
   */
  protectForeground?: boolean;
}

export class BackgroundEraserTool implements Tool {
  readonly id = "background-eraser";
  readonly shortcut = "E";

  settings: BackgroundEraserSettings = {
    size: 50,
    hardness: 0.5,
    opacity: 1,
    flow: 1,
    tolerance: 32,
    sampling: "continuous",
    limits: "discontiguous",
    protectForeground: false,
  };

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    sampledColor: { r: number; g: number; b: number } | null;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const found = findEraseTarget(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!found || !found.visible) return;
    if (found.lockAll) {
      ctx.showTip("Layer is locked.");
      return;
    }
    // A true raster layer's own Lock Image still blocks erasing. A Background
    // layer's `lockImage` flag instead represents "no transparency yet" — the
    // eraser itself is what triggers the Photoshop-style implicit conversion
    // to a normal layer below, so it must not be blocked by that flag (i83).
    if (found.kind === "raster" && found.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }
    const layer = convertBackgroundToLayer(found);

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    // Sample initial color
    const sampledColor = this.sampleColor(layer, docX, docY);

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      sampledColor,
    };

    this.erase(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    // Re-sample in continuous mode
    if (this.settings.sampling === "continuous") {
      this.active.sampledColor = this.sampleColor(this.active.layer, docX, docY);
    }

    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.erase(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private sampleColor(layer: RasterLayer, docX: number, docY: number): { r: number; g: number; b: number } | null {
    const px = Math.floor(docX);
    const py = Math.floor(docY);
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    if (px < 0 || py < 0 || px >= w || py >= h) return null;

    // Sample 3x3 area around center for better accuracy
    let r = 0, g = 0, b = 0, count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const sx = px + dx;
        const sy = py + dy;
        if (sx < 0 || sy < 0 || sx >= w || sy >= h) continue;
        const idx = (sy * w + sx) * 4;
        const a = layer.pixels.data[idx + 3]!;
        if (a > 0) {
          r += layer.pixels.data[idx]!;
          g += layer.pixels.data[idx + 1]!;
          b += layer.pixels.data[idx + 2]!;
          count++;
        }
      }
    }
    if (count === 0) return null;
    return { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) };
  }

  private erase(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.active.sampledColor) return;

    const { layer, layerSnapshot, sampledColor } = this.active;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.settings.size / 2;
    const r2 = radius * radius;
    const tolerance = this.settings.tolerance;
    const opacity = this.settings.flow;
    const limits = this.settings.limits;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    // For contiguous and find-edges modes, flood-fill from center to get the
    // connected set of matching pixels within the brush area.
    let contiguousMask: Uint8Array | null = null;
    if (limits === "contiguous" || limits === "find-edges") {
      contiguousMask = buildContiguousMask(
        data, w, h,
        Math.floor(docX), Math.floor(docY),
        sampledColor, tolerance,
        minX, minY, maxX, maxY,
      );
    }

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        const idx = (py * w + px) * 4;
        const a = data[idx + 3]!;
        if (a === 0) continue;

        // Check color distance
        const dr = data[idx]! - sampledColor.r;
        const dg = data[idx + 1]! - sampledColor.g;
        const db = data[idx + 2]! - sampledColor.b;
        const dist = Math.sqrt(dr * dr + dg * dg + db * db);

        if (dist > tolerance) continue;

        // Protect Foreground Color (i83): never erase pixels that match the
        // current foreground color within tolerance, regardless of how well
        // they match the sampled background color.
        if (this.settings.protectForeground) {
          const fdr = data[idx]! - ctx.fgColor.r;
          const fdg = data[idx + 1]! - ctx.fgColor.g;
          const fdb = data[idx + 2]! - ctx.fgColor.b;
          const fgDist = Math.sqrt(fdr * fdr + fdg * fdg + fdb * fdb);
          if (fgDist <= tolerance) continue;
        }

        // Contiguous: only erase pixels in the flood-connected set.
        if (limits === "contiguous" || limits === "find-edges") {
          if (!contiguousMask || !contiguousMask[py * w + px]) continue;
        }

        // Find-edges: skip pixels adjacent to a high-gradient boundary.
        if (limits === "find-edges") {
          if (isEdgePixel(data, w, h, px, py, tolerance)) continue;
        }

        // Soft-edge falloff
        const edgeDist = Math.sqrt(dx * dx + dy2) / radius;
        const falloff = edgeDist >= 1 ? 0 : edgeDist > this.settings.hardness
          ? (1 - edgeDist) / (1 - this.settings.hardness || 1e-6)
          : 1;

        const eraseAmount = falloff * opacity;
        if (eraseAmount <= 0) continue;

        // Erase: reduce alpha
        const newAlpha = Math.max(0, Math.round(a * (1 - eraseAmount)));
        data[idx + 3] = newAlpha;

        if (newAlpha < a) {
          if (px < touchX0) touchX0 = px;
          if (py < touchY0) touchY0 = py;
          if (px > touchX1) touchX1 = px;
          if (py > touchY1) touchY1 = py;
        }
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

/**
 * Build a mask of pixels that are (a) within [minX..maxX, minY..maxY],
 * (b) within the given color tolerance of sampledColor, and (c) 4-connected
 * to the seed pixel at (seedX, seedY). Pixels outside the brush bbox are
 * treated as barriers (not visited). Returns a w×h flat mask (non-zero = in set).
 */
function buildContiguousMask(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  seedX: number,
  seedY: number,
  sampledColor: { r: number; g: number; b: number },
  tolerance: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): Uint8Array {
  const mask = new Uint8Array(w * h);
  // Clamp seed to layer bounds
  const sx = Math.max(minX, Math.min(maxX, seedX));
  const sy = Math.max(minY, Math.min(maxY, seedY));
  if (sx < 0 || sy < 0 || sx >= w || sy >= h) return mask;

  if (!pixelMatchesSample(data, w, sx, sy, sampledColor, tolerance)) return mask;

  const queue: number[] = [];
  const seedPix = sy * w + sx;
  mask[seedPix] = 1;
  queue.push(seedPix);

  let head = 0;
  while (head < queue.length) {
    const p = queue[head++]!;
    const px = p % w;
    const py = (p / w) | 0;

    const neighbors = [
      px > minX ? p - 1 : -1,
      px < maxX ? p + 1 : -1,
      py > minY ? p - w : -1,
      py < maxY ? p + w : -1,
    ];
    for (const np of neighbors) {
      if (np < 0 || mask[np]) continue;
      const nx = np % w;
      const ny = (np / w) | 0;
      if (nx < minX || nx > maxX || ny < minY || ny > maxY) continue;
      if (pixelMatchesSample(data, w, nx, ny, sampledColor, tolerance)) {
        mask[np] = 1;
        queue.push(np);
      }
    }
  }
  return mask;
}

function pixelMatchesSample(
  data: Uint8ClampedArray,
  w: number,
  px: number,
  py: number,
  sampledColor: { r: number; g: number; b: number },
  tolerance: number,
): boolean {
  const idx = (py * w + px) * 4;
  if (data[idx + 3] === 0) return false;
  const dr = data[idx]! - sampledColor.r;
  const dg = data[idx + 1]! - sampledColor.g;
  const db = data[idx + 2]! - sampledColor.b;
  return Math.sqrt(dr * dr + dg * dg + db * db) <= tolerance;
}

/**
 * Returns true if the pixel at (px, py) is adjacent to a high-gradient
 * boundary — i.e., one of its 4-connected neighbors has a color significantly
 * different from it. This stops the find-edges eraser from crossing hard edges.
 */
function isEdgePixel(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  px: number,
  py: number,
  tolerance: number,
): boolean {
  const idx = (py * w + px) * 4;
  const r = data[idx]!, g = data[idx + 1]!, b = data[idx + 2]!;

  const neighbors = [
    px > 0 ? (py * w + px - 1) * 4 : -1,
    px < w - 1 ? (py * w + px + 1) * 4 : -1,
    py > 0 ? ((py - 1) * w + px) * 4 : -1,
    py < h - 1 ? ((py + 1) * w + px) * 4 : -1,
  ];

  // Edge detection threshold: use the tolerance as a proxy for what counts
  // as a "hard" edge — same scale as the color matching.
  const edgeThreshold = Math.max(16, tolerance * 0.5);

  for (const ni of neighbors) {
    if (ni < 0) continue;
    const na = data[ni + 3]!;
    if (na === 0) continue; // transparent neighbors are boundaries
    const nr = data[ni]!, ng = data[ni + 1]!, nb = data[ni + 2]!;
    const dr = nr - r, dg = ng - g, db = nb - b;
    const d = Math.sqrt(dr * dr + dg * dg + db * db);
    if (d > edgeThreshold) return true;
  }
  return false;
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

/**
 * Local variant of LayerUtils.findRaster that also matches a "background"
 * kind layer (audit i83). `document/LayerUtils.ts` is out of scope for this
 * fix, so this duplicates its recursive-lookup shape rather than editing the
 * shared helper.
 */
function findEraseTarget(layers: readonly Layer[], id: string): RasterLayer | BackgroundLayer | null {
  for (const l of layers) {
    if (l.id === id && (l.kind === "raster" || l.kind === "background")) return l;
    if (l.kind === "group") {
      const inner = findEraseTarget(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Photoshop converts a Background layer to a normal layer the instant a tool
 * would introduce transparency into it (erasing, cutting, etc.) — the lock
 * icon disappears and it behaves like any other layer from then on. We
 * mirror that here by flipping `kind` to "raster" and dropping the
 * position/image locks a Background layer carries by default, in place, so
 * the erase can proceed and produce real transparency instead of being
 * blocked or silently doing nothing.
 *
 * Scope note: this mutates the layer object in place rather than going
 * through a Command. `PaintStrokeCommand`'s own layer lookup (see
 * src/core/commands/PaintStrokeCommand.ts) only matches `kind === "raster"`,
 * and that file — along with the History/Command infrastructure needed to
 * make the conversion itself a proper, separately-undoable step — is out of
 * scope for this fix (task limited to BackgroundEraserTool.ts/MagicEraserTool.ts).
 * Practically: the Background→normal conversion is not itself on the undo
 * stack (undoing the erase stroke restores pixels but the layer stays a
 * normal layer afterward), the same way live pixel mutations during a drag
 * are already applied before the PaintStrokeCommand is emitted. A fully
 * undoable conversion needs a dedicated Command and is follow-up work.
 */
function convertBackgroundToLayer(layer: RasterLayer | BackgroundLayer): RasterLayer {
  if (layer.kind === "background") {
    const mutable = layer as unknown as { kind: string; lockImage: boolean; lockPosition: boolean };
    mutable.kind = "raster";
    mutable.lockImage = false;
    mutable.lockPosition = false;
  }
  return layer as RasterLayer;
}
